import { Box, Button, Typography } from "@mui/material";
import { useQuery } from "react-query";
import StatsCard from "src/components/common/StatsCard";

import defaultStyles, { StylesClasses } from "../styles";
import { getStyles } from "src/styles/theme";
import {
  getLandDashboardDetails,
  getTableDashboardDetails,
} from "src/utils/api/dashboard";
import { DashBoardTable } from "src/components/common/DashBoardTable";
import {
  downloadSheet,
  exportTableToXlsx,
  formatData,
  formatDataForBarChart,
} from "src/utils/helper";
import DashboardChartSkeleton from "src/components/common/DashboardChartSkeleton";
import { useEffect, useMemo, useState } from "react";
import MultiSelect from "src/components/common/MultiSelect";
import { getStates } from "src/utils/api/roles";
import StatusBarChart from "src/components/common/StatusBarChart";
import RenderTable from "../../Home/RenderTable";
import { costEstimateDashboardtableColumns } from "src/constants/tableColumns";
import { AxiosResponse } from "axios";
import { usePersistedFilters } from "src/hooks/usePersistedFilters";

const CostEstimateDashboard = () => {
  const [isTabLoading, setIsTabLoading] = useState(true);

  const [states, setStates] = useState<any>([]);

  const [tableRows, setTableRows] = useState([]);

  const [filtersState, setFiltersState] = useState([]);

  const { persisted, saveFilters } = usePersistedFilters("mw_costEstimate");

  const [queryParams, setQueryParams] = useState<{
    projectStateIds?: string[];
    type: string;
  }>({
    projectStateIds: persisted?.queryParams?.projectStateIds ?? undefined,
    type: "landOwnership",
  });

  const [filterKey, setFilterKey] = useState(
    persisted?.filterKey ?? { projectStateIds: null }
  );

  const { data: dashboard, isLoading: dashboardLoading } = useQuery(
    ["landDashboard", queryParams],
    () => getLandDashboardDetails(queryParams),
    {
      onSuccess: () => {},
      onError: () => {},
      refetchOnMount: true,
    }
  );

  const { data: tableDashboard, isLoading } = useQuery(
    ["table-landOwnership", queryParams],
    () => getTableDashboardDetails(queryParams),
    {
      onSuccess: (res: AxiosResponse) => {
        setTableRows(
          res?.data?.stats.map((items, idx) => ({
            id: idx,
            sno: idx + 1,
            state: items.state,
            totalAreaGenerateDemandNote: items.totalAreaGenerateDemandNote,
            totalNumberGenerateDemandNote: items.totalNumberGenerateDemandNote,
            totalNumberVerifyDemandNote: items.totalNumberVerifyDemandNote,
            totalAreaVerifyDemandNote: items.totalAreaVerifyDemandNote,
            totalAreaUnderQuery: items.totalAreaUnderQuery,
            totalNumberUnderQuery: items.totalNumberUnderQuery,
            totalAreaNodalUnderQuery: items.totalAreaNodalUnderQuery,
            totalNumberNodalUnderQuery: items.totalNumberNodalUnderQuery,
            totalAreaPaymentPending: items.totalAreaPaymentPending,
            totalNumberPaymentPending: items.totalNumberPaymentPending,
            totalAreaPaymentDone: items.totalAreaPaymentDone,
            totalNumberPaymentDone: items.totalNumberPaymentDone,
            totalAreaRegistered: items.totalAreaRegistered,
            totalNumberRegistered: items.totalNumberRegistered,
            totalNumberDemandNoteGenerated:
              items.totalNumberDemandNoteGenerated,
            totalAreaDemandNoteGenerated: items.totalAreaDemandNoteGenerated,
            totalNumberDeselected: items.totalNumberDeselected,
            totalAreaDeselected: items.totalAreaDeselected,
          }))
        );
        setIsTabLoading(false);
      },
      onError: () => {},
      refetchOnMount: true,
    }
  );

  useQuery(["getStatesType"], () => getStates(), {
    onSuccess: (data) => {
      setStates(data.data);
    },
    refetchOnMount: true,
  });

  const formattedData = useMemo(
    () => formatDataForBarChart(dashboard?.data?.stats),
    [dashboard?.data?.stats]
  );

  const routesConfig = {
    All: "/",
    "GENERATE_DEMAND_NOTE,REGENERATE_DEMAND_NOTE":
      "/?currentTab=GENERATE_DEMAND_NOTE,REGENERATE_DEMAND_NOTE",
    VERIFY_DEMAND_NOTE: "/?currentTab=VERIFY_DEMAND_NOTE",
    UNDER_QUERY: "/?currentTab=UNDER_QUERY",
    PAYMENT_PENDING: "/?currentTab=PAYMENT_PENDING",
    "VERIFICATION_PENDING,APPROVED": "/?currentTab=APPROVED",
  };

  const dashboardTableData: DashBoardTable = {
    topHeader: [
      "",
      "ALL SELECTED",
      "COST ESTIMATE PENDING",
      "SNO QUERY",
      "VERIFY COST ESTIMATE",
      "ADMIN QUERY",
      "PAYMENT PENDING",
      "PAYMENT DONE",
      "COST ESTIMATE RECEIVED",
      "DESELECTED",
    ],
    header: [
      "S.No",
      "State",
      "No",
      "Area (ha)",
      "No",
      "Area (ha)",
      "No",
      "Area (ha)",
      "No",
      "Area (ha)",
      "No",
      "Area (ha)",
      "No",
      "Area (ha)",
      "No",
      "Area (ha)",
      "No",
      "Area (ha)",
      "No",
      "Area (ha)",
    ],
    data: tableRows,
  };
  const styles = getStyles(defaultStyles);

  useEffect(() => {
    setFiltersState(dashboard?.data?.state);
  }, [dashboard]);

  useEffect(() => {
    saveFilters({ queryParams, filterKey });
  }, [queryParams, filterKey]);

  useEffect(() => {
    setIsTabLoading(true);
  }, []);

  return (
    <>
      <Box {...styles("topHeader")}>
        <Box
          sx={{
            marginBottom: "5px",
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {filtersState && (
              <Box
                sx={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                Selected States:-
                {filtersState?.map((filterState) => {
                  return (
                    <Box
                      sx={{
                        height: "46px",
                        padding: "11px",
                        width: "fit-content",
                        border: "1px solid grey",
                        borderRadius: "24px",
                        backgroundColor: "#eee",
                      }}
                    >
                      {filterState}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: "8px" }}>
            <MultiSelect
              options={states}
              value={filterKey.projectStateIds}
              onChange={(e: any) => {
                setFilterKey((prevFilter) => {
                  const newState = {
                    ...prevFilter,
                    projectStateIds: e.length ? e : null,
                  };

                  setQueryParams({
                    ...queryParams,
                    projectStateIds: newState?.projectStateIds,
                  });

                  return newState;
                });
              }}
            />
          </Box>
        </Box>

        {!dashboardLoading ? (
          <Box sx={{ width: "90%", marginBottom: "20px", alignSelf: "center" }}>
            <StatusBarChart
              data={formattedData}
              label={"Status of Cost Estimates of Eco-Restoration Blocks"}
            />
          </Box>
        ) : (
          <></>
        )}
        <Box {...styles("statsCardWrapper")}>
          {dashboard?.data?.stats?.map((stat, index) => (
            <StatsCard
              key={index}
              title={`${stat?.description?.toUpperCase()}`}
              value={`${stat?.number}`}
              area={stat?.totalArea}
              customStyles={{ valueCard: styles("statsCard").sx }}
              link={routesConfig[stat?.type]}
            />
          ))}
        </Box>
      </Box>

      {isLoading || isTabLoading ? (
        <DashboardChartSkeleton />
      ) : (
        <Box {...styles("dashboardTableWrapper")}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="h5" {...styles("heading")}>
              Status of Cost Estimate and Demand Note Under GCP
            </Typography>
            <Button
              variant="outlined"
              onClick={() =>
                exportTableToXlsx(
                  dashboardTableData.data,
                  dashboardTableData.header,
                  dashboardTableData.topHeader
                )
              }
            >
              Export
            </Button>
          </Box>
          <DashBoardTable {...dashboardTableData} />
        </Box>
      )}
    </>
  );
};

export default CostEstimateDashboard;
